import { TaxStrategy } from '@principle-theorem/accounting';
import { AtLeast, Region } from '@principle-theorem/shared';
import { ChartableSurface } from '../../core/chartable-surface';
import {
  IServiceCode,
  IServiceProvider,
  ItemCodeAliases,
  ServiceCodeType,
} from '../service-item';

// HICAPS Physiotherapy item numbers per HICAPS Provider Item Guide.
// Unlike dental, there is no national physiotherapy fee schedule in Australia.
// HICAPS is a claiming mechanism, not a pricing authority. Each practice sets
// their own fees. All HICAPS items default to $0; practices override as needed.
// Medicare item 10960 — Chronic Condition Management (CCM) physiotherapy.

export const BASE_PHYSIOTHERAPY_CODES: Record<
  string,
  AtLeast<IServiceCode, 'code' | 'title' | 'fee'>
> = {
  500: {
    code: 500,
    title: 'Assessment Consultation (Practice)',
    description:
      'Initial physiotherapy assessment and consultation conducted in a practice setting',
    fee: 0,
  },
  505: {
    code: 505,
    title: 'Subsequent Consultation (Practice)',
    description:
      'Subsequent physiotherapy consultation conducted in a practice setting',
    fee: 0,
  },
  506: {
    code: 506,
    title: 'Long Subsequent Consultation — 2 Areas (Practice)',
    description:
      'Extended subsequent physiotherapy consultation for 2 body areas conducted in a practice setting',
    fee: 0,
  },
  520: {
    code: 520,
    title: 'Assessment Consultation (Hospital)',
    description:
      'Initial physiotherapy assessment and consultation conducted in a hospital setting',
    fee: 0,
  },
  525: {
    code: 525,
    title: 'Subsequent Consultation (Hospital)',
    description:
      'Subsequent physiotherapy consultation conducted in a hospital setting',
    fee: 0,
  },
  526: {
    code: 526,
    title: 'Long Subsequent Consultation — 2 Areas (Hospital)',
    description:
      'Extended subsequent physiotherapy consultation for 2 body areas conducted in a hospital setting',
    fee: 0,
  },
  530: {
    code: 530,
    title: 'Assessment Consultation (Home)',
    description:
      'Initial physiotherapy assessment and consultation conducted in a home setting',
    fee: 0,
  },
  535: {
    code: 535,
    title: 'Subsequent Consultation (Home)',
    description:
      'Subsequent physiotherapy consultation conducted in a home setting',
    fee: 0,
  },
  536: {
    code: 536,
    title: 'Long Subsequent Consultation — 2 Areas (Home)',
    description:
      'Extended subsequent physiotherapy consultation for 2 body areas conducted in a home setting',
    fee: 0,
  },
  560: {
    code: 560,
    title: 'Group Consultation',
    description: 'Physiotherapy group consultation',
    fee: 0,
  },
  561: {
    code: 561,
    title: 'Class Consultation',
    description: 'Physiotherapy class consultation',
    fee: 0,
  },
  570: {
    code: 570,
    title: 'Lymphoedema Treatment',
    description: 'Physiotherapy lymphoedema treatment',
    fee: 0,
  },
  582: {
    code: 582,
    title: 'Neurological Rehabilitation',
    description: 'Physiotherapy neurological rehabilitation',
    fee: 0,
  },
  591: {
    code: 591,
    title: 'Custom Made Orthosis — Upper Limb',
    description:
      'Custom made orthosis for upper limb — price determined by provider',
    fee: 0,
  },
  592: {
    code: 592,
    title: 'Custom Made Orthosis — Lower Limb',
    description:
      'Custom made orthosis for lower limb — price determined by provider',
    fee: 0,
  },
  593: {
    code: 593,
    title: 'Pelvic Floor Physiotherapy',
    description: 'Pelvic floor physiotherapy treatment',
    fee: 0,
  },
  595: {
    code: 595,
    title: 'Antenatal Exercise Class',
    description: 'Physiotherapy antenatal exercise class',
    fee: 0,
  },
  596: {
    code: 596,
    title: 'Postnatal Exercise Class',
    description: 'Physiotherapy postnatal exercise class',
    fee: 0,
  },
  10960: {
    code: 10960,
    title: 'Physiotherapy — Chronic Condition Management',
    description:
      'Physiotherapy health service under Chronic Condition Management (CCM) plan. Max 5 services/calendar year. Requires GP referral under CCM plan. Must be ≥20 mins duration.',
    fee: 72.65,
    taxStatus: TaxStrategy.GSTFree,
  },
};

export const PHYSIOTHERAPY_CODE_MAP: ItemCodeAliases = Object.entries(
  BASE_PHYSIOTHERAPY_CODES
).reduce(
  (codes, [key, code]) => ({
    ...codes,
    [key]: {
      scope: [ChartableSurface.Unscoped],
      rules: {},
      deleted: false,
      taxStatus: TaxStrategy.GSTPossible,
      ...code,
      type: ServiceCodeType.Physiotherapy,
    },
  }),
  {}
);

export const PHYSIOTHERAPY_SERVICE_CODES: IServiceCode[] = Object.values(
  PHYSIOTHERAPY_CODE_MAP
);

export const PHYSIOTHERAPY_SERVICE: IServiceProvider = {
  label: 'Physiotherapy',
  description: 'HICAPS Physiotherapy and Medicare CDM Fee Schedule',
  type: ServiceCodeType.Physiotherapy,
  items: PHYSIOTHERAPY_CODE_MAP,
  region: Region.Australia,
};
